import React, { useState } from "react";
import styled from "styled-components";
import { useHovered } from "@sonder/ui/source/hooks/useHovered";
import { Switch } from "@sonder/ui/source/styled/components/Switch";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import {
  ChecklistItemDelete,
  ChecklistItem as ChecklistItemFragment,
  ChecklistItemTextArea,
  ChecklistItemToggle,
} from "@sonder/ui/source/styled/fragments/Checklist";
import { colors } from "@sonder/ui/source/styled/theme";

export interface ChecklistItemBaseProps {
  createdAt?: string;
  id?: string;
  isChecked?: boolean;
  name?: string;
  required?: boolean;
}

const StyledSwitch = styled(Switch)`
  margin: 0 1.5rem 0 1rem;
`;

const StyledChecklistWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0.5rem 0;
`;

const StyledChecklistItemError = styled.div`
  color: ${colors.redD};
  margin-top: 0.5rem;
`;

interface ChecklistItemProps extends ChecklistItemBaseProps {
  children?: string;
  onChange?: (checkListItem: ChecklistItemBaseProps) => void;
  onDelete?: (checkListItem: ChecklistItemBaseProps) => void;
  onToggle?: (checkListItem: ChecklistItemBaseProps) => void;
}

export const ChecklistItem = ({
  children,
  id,
  isChecked,
  onChange = () => null,
  onDelete = () => null,
  onToggle = () => null,
  required = false,
}: ChecklistItemProps) => {
  const [validationError, setValidationError] = useState("");
  const [focused, setFocused] = useState(false);
  const { hovered, onMouseEnter, onMouseLeave } = useHovered();
  return (
    <StyledChecklistWrapper
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <ChecklistItemFragment>
        <ChecklistItemToggle
          isChecked={isChecked}
          onClick={() => onToggle({ id, isChecked })}
        />
        <ChecklistItemTextArea
          error={!!validationError || undefined}
          required={required}
          onBlur={() => setFocused(false)}
          onChange={newName => {
            if (newName && newName.trim() !== "") {
              setValidationError("");
              onChange({ id, name: newName });
            } else setValidationError("Name cannot be blank");
          }}
          onFocus={() => {
            setValidationError("");
            setFocused(true);
          }}
        >
          {children}
        </ChecklistItemTextArea>
        <StyledSwitch
          checked={required}
          title="When enabled, this item must be checked to complete the task."
          onChange={() => onChange({ id, required: !required })}
        />
        <ChecklistItemDelete
          isVisible={hovered || focused || !!validationError}
          onClick={() => {
            setValidationError("");
            onDelete({ id });
          }}
        />
      </ChecklistItemFragment>
      {validationError && (
        <StyledChecklistItemError>{validationError}</StyledChecklistItemError>
      )}
    </StyledChecklistWrapper>
  );
};

interface ChecklistFooterProps {
  disabled?: boolean;
  onAdd?: (checkListItem: ChecklistItemBaseProps) => void;
}

export const ChecklistFooter = ({
  disabled,
  onAdd = () => null,
}: ChecklistFooterProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [validationError, setValidationError] = useState("");
  return (
    <StyledChecklistWrapper>
      <ChecklistItemFragment>
        {!isAdding && (
          <Button disabled={disabled} onClick={() => setIsAdding(true)}>
            Add item
          </Button>
        )}
        {isAdding && (
          <ChecklistItemTextArea
            autoFocus
            disabled={disabled}
            error={!!validationError || undefined}
            onChange={newName => {
              if (newName && newName.trim() !== "") {
                onAdd({ name: newName, required: false });
                setValidationError("");
                setIsAdding(false);
              } else setValidationError("Name cannot be blank");
            }}
            onFocus={() => setValidationError("")}
          />
        )}
        {isAdding && (
          <ChecklistItemDelete
            isVisible
            onClick={() => {
              setValidationError("");
              setIsAdding(false);
            }}
          />
        )}
      </ChecklistItemFragment>
      {validationError && (
        <StyledChecklistItemError>{validationError}</StyledChecklistItemError>
      )}
    </StyledChecklistWrapper>
  );
};
